import React, { useEffect, useMemo, useState } from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import {
  Extension as ModuleIcon,
  Tune as SettingsIcon,
} from "@mui/icons-material";
import { COLORS, TYPO, CARD_PAD, CARD_GAP, CARD_PAGE_PAD, dashboardSectionSx } from "../../styles/uiConstants";
import { ActionButton, ChoiceChipGroup } from "../atoms";
import SectionCard from "../molecules/SectionCard";
import ModuleTile from "../molecules/ModuleTile";
import {
  PROFILE_OPTIONS,
  TERM_SET_OPTIONS,
  enableModule,
  disableModule,
} from "../../constants/clinicModules";

const CORE_SUMMARY = "Hastalar · Randevular · Personel · Klinik ayarları · Raporlar";
const OTHER_GROUP = { key: "other", label: "Diğer" };

/**
 * Klinik modül ayarları — admin (izin verilen modüller + profil) ve klinik
 * yöneticisi (izin verilenler içinde kapatma) için ortak panel.
 *
 * Modüller gruplu, seçilebilir kutular olarak listelenir. Bağımlılıklar
 * kendiliğinden ayarlanır: bir modül açılınca gerektirdikleri de açılır,
 * kapatılınca ona bağlı olanlar da kapanır.
 *
 * @param {"admin"|"clinic"} mode
 * @param {object|null} modules  Sunucu görünümü: { profiles, termSet, allowedModules,
 *                                disabledModules, activeModules, catalog, groups, profileCatalog }
 * @param {boolean} loading
 * @param {boolean} saving
 * @param {boolean} canEdit
 * @param {(payload) => Promise} onSave  admin: { profiles, allowedModules, termSet }
 *                                        clinic: { disabledModules, termSet }
 */
export default function ClinicModulesPanel({
  mode = "admin",
  modules,
  loading,
  saving,
  canEdit = true,
  palette,
  onSave,
}) {
  const isAdmin = mode === "admin";
  const catalog = useMemo(() => modules?.catalog || [], [modules]);
  const byKey = useMemo(() => Object.fromEntries(catalog.map((m) => [m.key, m])), [catalog]);

  // Düzenlenen küme: admin'de izin verilenler, klinikte açık olanlar.
  const [enabled, setEnabled] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [termSet, setTermSet] = useState("clinical");
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    if (!modules) return;
    setProfiles(modules.profiles || []);
    setTermSet(modules.termSet || "clinical");
    setNotice(null);
    if (isAdmin) {
      setEnabled(modules.allowedModules || []);
    } else {
      const disabled = new Set(modules.disabledModules || []);
      setEnabled((modules.allowedModules || []).filter((k) => !disabled.has(k)));
    }
  }, [modules, isAdmin]);

  const enabledSet = useMemo(() => new Set(enabled), [enabled]);
  const allowedSet = useMemo(() => new Set(modules?.allowedModules || []), [modules]);

  const dirty = useMemo(() => {
    if (!modules) return false;
    const before = isAdmin
      ? modules.allowedModules || []
      : (modules.allowedModules || []).filter((k) => !(modules.disabledModules || []).includes(k));
    const same = (a, b) => [...a].sort().join(",") === [...b].sort().join(",");
    return (
      !same(before, enabled) ||
      termSet !== (modules.termSet || "clinical") ||
      (isAdmin && !same(modules.profiles || [], profiles))
    );
  }, [modules, enabled, termSet, profiles, isAdmin]);

  const labelsOf = (keys) => keys.map((k) => byKey[k]?.label || k).join(", ");

  // Seçilen türlerin modül birleşimi ve uygun hitap (yalnız wellness türleri → wellness).
  const profileDefaults = (keys) => {
    const defs = (modules?.profileCatalog || []).filter((p) => keys.includes(p.key));
    const mods = new Set(defs.flatMap((p) => p.modules));
    return {
      modules: catalog.map((m) => m.key).filter((k) => mods.has(k) || byKey[k]?.locked),
      termSet: defs.length && defs.every((p) => p.termSet === "wellness") ? "wellness" : "clinical",
    };
  };

  const applyProfiles = (keys) => {
    setProfiles(keys);
    const d = profileDefaults(keys);
    setEnabled(d.modules);
    setTermSet(d.termSet);
    setNotice("Modüller seçilen klinik türlerine göre yeniden işaretlendi.");
  };

  // Modüller türlerin önerdiğinden farklıysa (elle değiştirildiyse) küçük bir not gösterilir.
  const customized = useMemo(() => {
    if (!isAdmin || !modules?.profileCatalog) return false;
    const d = profileDefaults(profiles);
    return [...d.modules].sort().join(",") !== [...enabled].sort().join(",");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, modules, profiles, enabled]);

  const toggle = (m) => {
    const label = m.label;
    if (enabledSet.has(m.key)) {
      const { next, removed } = disableModule(enabled, m.key, catalog);
      setEnabled(next);
      setNotice(removed.length ? `${label} kapatıldı; ona bağlı ${labelsOf(removed)} de kapatıldı.` : null);
    } else {
      const { next, added } = enableModule(enabled, m.key, catalog);
      setEnabled(next);
      setNotice(added.length ? `${label} için ${labelsOf(added)} de açıldı.` : null);
    }
  };

  const handleSave = () => {
    if (isAdmin) {
      onSave?.({ profiles, allowedModules: enabled, termSet });
    } else {
      const disabledModules = (modules?.allowedModules || []).filter(
        (k) => !enabledSet.has(k) && !byKey[k]?.locked
      );
      onSave?.({ disabledModules, termSet });
    }
  };

  // Klinik yöneticisi yalnız paketindekileri açıp kapatır; gereksinimi pakette
  // olmayan modül de açılamaz.
  const unavailableReason = (m) => {
    if (isAdmin) return null;
    if (!allowedSet.has(m.key)) return "Paketinizde yok — açmak için Pupill ile iletişime geçin.";
    const missing = (m.requires || []).filter((r) => !allowedSet.has(r));
    if (missing.length) return `${labelsOf(missing)} paketinizde olmadığı için açılamaz.`;
    return null;
  };

  const groups = useMemo(() => {
    const defs = [...(modules?.groups || []), OTHER_GROUP];
    const known = new Set(defs.map((g) => g.key));
    const optional = catalog.filter((m) => !m.locked);
    return defs
      .map((g) => ({
        ...g,
        items: optional.filter((m) =>
          g.key === OTHER_GROUP.key ? !known.has(m.group) || m.group === OTHER_GROUP.key : m.group === g.key
        ),
      }))
      .filter((g) => g.items.length);
  }, [modules, catalog]);

  const activeCount = catalog.filter((m) => !m.locked && enabledSet.has(m.key)).length;
  const optionalCount = catalog.filter((m) => !m.locked).length;

  const saveAction = canEdit ? (
    <ActionButton
      variant="save"
      compact
      palette={palette}
      loading={saving}
      disabled={loading || !dirty}
      onClick={handleSave}
    />
  ) : null;

  const fieldLabel = (text) => (
    <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500, mb: 0.75 }}>{text}</Typography>
  );

  return (
    // Kartlı ayar sekmesi (design-principles §8.1, §8.2): başlık şeridi hizası + token aralıklar
    <Box sx={{ ...CARD_PAGE_PAD, bgcolor: COLORS.slate50, minHeight: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column", gap: CARD_GAP }}>
      <SectionCard
        icon={<SettingsIcon sx={{ fontSize: 18 }} />}
        sx={dashboardSectionSx}
        title={isAdmin ? "Klinik türü" : "Genel"}
        subtitle={
          isAdmin
            ? "Bir veya birden fazla tür seçin; modüller seçilen türlere göre işaretlenir, sonra tek tek değiştirebilirsiniz."
            : "Değişiklikler Kaydet'e bastığınızda uygulanır."
        }
        action={saveAction}
      >
        <Box sx={{ p: CARD_PAD, display: "flex", flexDirection: "column", gap: 2 }}>
          {loading ? (
            <>
              <Skeleton variant="rounded" width={360} height={30} />
              <Skeleton variant="rounded" width={420} height={30} />
            </>
          ) : (
            <>
              {isAdmin && (
                <Box>
                  {fieldLabel("Klinik türü (birden fazla seçilebilir)")}
                  <ChoiceChipGroup
                    options={PROFILE_OPTIONS}
                    value={profiles}
                    onChange={applyProfiles}
                    multiple
                    required
                    disabled={!canEdit}
                    palette={palette}
                    ariaLabel="Klinik türü"
                  />
                  {customized && (
                    <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500, mt: 0.75 }}>
                      Modüller seçilen türlerin önerisinden farklı — elle değiştirildi.
                    </Typography>
                  )}
                </Box>
              )}
              <Box>
                {fieldLabel("Panelde kişilere ve görüşmelere nasıl hitap edilsin?")}
                <ChoiceChipGroup
                  options={TERM_SET_OPTIONS}
                  value={termSet}
                  onChange={setTermSet}
                  disabled={!canEdit}
                  palette={palette}
                  ariaLabel="Hitap"
                />
              </Box>
              <Box>
                {fieldLabel("Her klinikte açık")}
                <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate700 }}>{CORE_SUMMARY}</Typography>
              </Box>
            </>
          )}
        </Box>
      </SectionCard>

      <SectionCard
        icon={<ModuleIcon sx={{ fontSize: 18 }} />}
        sx={dashboardSectionSx}
        title={loading ? "Modüller" : `Modüller · ${activeCount}/${optionalCount} açık`}
        subtitle={
          isAdmin
            ? "Klinikte kullanılabilecek özellikleri işaretleyin. İşaretsiz olanlar menüde ve sekmelerde görünmez."
            : "Kullanmadığınız özelliklerin işaretini kaldırın. Kayıtlar silinmez; tekrar işaretleyince geri gelir."
        }
      >
        <Box sx={{ p: CARD_PAD, display: "flex", flexDirection: "column", gap: 2.5 }}>
          {notice && (
            <Typography
              role="status"
              sx={{
                fontSize: TYPO.label,
                color: COLORS.slate700,
                bgcolor: COLORS.slate50,
                border: `1px solid ${COLORS.slate200}`,
                borderRadius: 1.5,
                px: 1.5,
                py: 1,
              }}
            >
              {notice}
            </Typography>
          )}
          {loading
            ? Array.from({ length: 2 }).map((_, i) => (
                <Box key={i}>
                  <Skeleton variant="text" width={160} />
                  <Box sx={tileGridSx}>
                    {Array.from({ length: 3 }).map((__, j) => (
                      <Skeleton key={j} variant="rounded" height={92} />
                    ))}
                  </Box>
                </Box>
              ))
            : groups.map((g) => (
                <Box key={g.key}>
                  <Typography sx={{ fontSize: TYPO.sectionTitle, fontWeight: 500, color: COLORS.slate700, mb: 1 }}>
                    {g.label}
                  </Typography>
                  <Box sx={tileGridSx}>
                    {g.items.map((m) => {
                      const reason = unavailableReason(m);
                      return (
                        <ModuleTile
                          key={m.key}
                          label={m.label}
                          description={m.description}
                          where={m.where}
                          checked={enabledSet.has(m.key)}
                          disabled={!canEdit || Boolean(reason)}
                          note={reason}
                          onToggle={() => toggle(m)}
                          palette={palette}
                        />
                      );
                    })}
                  </Box>
                </Box>
              ))}
        </Box>
      </SectionCard>
    </Box>
  );
}

const tileGridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" },
  gap: 1.25,
};
